import { toast } from "@/components/ui";
import { useVisitDiagnoses } from "@/features/Charting/network/useVisitDiagnoses";
import { usePatientDemographics } from "@/features/Patient/PatientBar/network/usePatientDemographics";
import { MessageSeverity, captureMessage } from "@/lib/monitoring/monitoring";
import { cleanObject } from "@/lib/utils";
import { constants, getCoding } from "@curai/js-fhir-tools";
import { Pill } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useCreatePrescription } from "../../../network/useCreatePrescription";
import { SuggestionGroup } from "./SuggestionGroup";
import { useGetRxRecommendations } from "./network/useGetRxRecommendations";
import type { RxRecommendation } from "./types";

interface ProtocolSuggestionProps {
  patientId: string;
  encounterId: string;
  carePlanId: string;
  eocId?: string;
  currentPrescriptionCodes: string[];
}

// Derives the display name for a group by stripping the strength from the
// medication name. e.g. "Wegovy Oral Tablet 2.4 MG" → "Wegovy Oral Tablet".
const getBaseName = (medicationName: string, strength: string): string =>
  medicationName.replace(strength, "").trim();

// Groups recommendations by base name + order sentence fields so that multiple
// strengths of the same drug form (e.g. "Wegovy Oral Tablet") are shown together
// under one collapsible. Items with differing daysSupply, dispenseQuantity, or
// refills are placed in separate groups even if the base name matches.
// Insertion order is preserved via Map, matching the API's sort order.
const groupRecommendations = (
  items: RxRecommendation[],
): { key: string; baseName: string; suggestions: RxRecommendation[] }[] => {
  const groupMap = new Map<
    string,
    { baseName: string; suggestions: RxRecommendation[] }
  >();
  for (const item of items) {
    const baseName = getBaseName(item.medicationName, item.strength);
    const { daysSupply, dispenseQuantity, refills } = item.orderSentence ?? {};
    const key = `${baseName}|${daysSupply ?? ""}|${dispenseQuantity ?? ""}|${refills ?? ""}`;
    const existing = groupMap.get(key);
    if (existing) {
      existing.suggestions.push(item);
    } else {
      groupMap.set(key, { baseName, suggestions: [item] });
    }
  }
  return Array.from(groupMap.entries()).map(([key, group]) => ({
    key,
    ...group,
  }));
};

export const ProtocolSuggestion = ({
  patientId,
  encounterId,
  carePlanId,
  eocId,
  currentPrescriptionCodes,
}: ProtocolSuggestionProps) => {
  // Track which suggestion is currently being added (by drug code)
  const [addingDrugCode, setAddingDrugCode] = useState<string | null>(null);
  const { data: patientDemographics } = usePatientDemographics(patientId);
  const { data: visitDiagnoses } = useVisitDiagnoses(encounterId, !eocId);

  const snomedCodes = useMemo(
    () =>
      visitDiagnoses?.visitDiagnoses.flatMap((visitDiagnosis) => {
        const code = visitDiagnosis.code
          ? getCoding(visitDiagnosis.code, constants.SNOMED_SYSTEM_URL)?.code
          : undefined;

        return code ? [code] : [];
      }) ?? [],
    [visitDiagnoses?.visitDiagnoses],
  );
  const hasVisitDiagnoses = (visitDiagnoses?.visitDiagnoses.length ?? 0) > 0;
  const partnerUid = hasVisitDiagnoses
    ? patientDemographics?.membership?.partnerUids[0]
    : undefined;
  const recommendationEocId = hasVisitDiagnoses ? undefined : eocId;
  const recommendationSnomedCodes = hasVisitDiagnoses ? snomedCodes : undefined;

  const {
    data: recommendations,
    isLoading,
    isError,
  } = useGetRxRecommendations({
    patientId,
    eocId: recommendationEocId,
    partnerUid,
    snomedCodes: recommendationSnomedCodes,
  });

  const createPrescription = useCreatePrescription({
    patientId,
    encounterId,
  });

  // Log to Sentry if fetching recommendations fails
  useEffect(() => {
    if (isError) {
      captureMessage(
        "Failed to fetch protocol suggestions",
        MessageSeverity.Warning,
        { eocId },
      );
    }
  }, [isError, eocId]);

  const handleAddPrescription = async (suggestion: RxRecommendation) => {
    const drugCode = suggestion.drugCodes?.[0]?.code;
    if (!drugCode) {
      toast.error("Unable to add prescription - missing drug code");
      return;
    }
    setAddingDrugCode(drugCode);

    try {
      const { drugCodes, orderSentence, medicationName } = suggestion;

      await createPrescription.mutateAsync({
        patientId,
        encounterId,
        carePlanId,
        medication: {
          coding:
            drugCodes?.map((coding) => ({
              system: coding.codeSystem,
              code: coding.code,
              display: medicationName,
            })) || [],
          text: medicationName,
        },
        prescription: cleanObject({
          dispense: cleanObject({
            daysSupply: orderSentence?.daysSupply,
            quantity: orderSentence?.dispenseQuantity,
            unit: orderSentence?.dispenseQuantityUnit,
            refills: orderSentence?.refills,
          }),
          sig: orderSentence?.directions,
          noteToPharmacy: orderSentence?.noteToPharmacy,
          substitutionAllowed: orderSentence?.substitutionAllowed,
        }),
      });
    } catch {
      toast.error("Failed to add prescription. Please try again.");
    } finally {
      setAddingDrugCode(null);
    }
  };

  const isAlreadyAdded = (suggestion: RxRecommendation) => {
    return suggestion.drugCodes?.some((drugCode) =>
      currentPrescriptionCodes.includes(drugCode.code),
    );
  };

  // Don't show anything while loading, on error, or when empty
  // (90%+ of visits won't have protocol suggestions)
  if (isLoading || isError || !recommendations?.items?.length) {
    return null;
  }

  const groups = groupRecommendations(recommendations.items);

  return (
    <div className="rounded-lg bg-superclinical p-3 w-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Pill className="h-4 w-4 text-superclinical" />
        <span className="text-xs font-bold text-superclinical tracking-wide">
          Protocol Suggestion
        </span>
      </div>

      {/* Suggestion groups */}
      <div className="flex flex-col">
        {groups.map(({ key, baseName, suggestions }) => (
          <SuggestionGroup
            key={key}
            baseName={baseName}
            suggestions={suggestions}
            onAdd={handleAddPrescription}
            addingDrugCode={addingDrugCode}
            isAlreadyAdded={isAlreadyAdded}
          />
        ))}
      </div>
    </div>
  );
};
